import axios from "axios";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import styled, { useTheme } from "styled-components/macro";
import ReactTooltip from "react-tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import { endpoints } from "../../../endpoints";
import {
  useStoreState,
  convertUserToOption,
  rowHover,
} from "../../../util/util";
import type {
  LeadAssignee,
  OptionType,
  User,
  UUID,
} from "../../../types/types";
import { PrimaryButtonFitContainer } from "../../../components/Buttons/Buttons";
import { SearchSelectInfiniteScroll } from "../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { Form } from "../../../layout/FormLayout";
import { Table } from "../../../components/Table/Table";
import { InfoIcon, DeleteIcon } from "../../../components/Icons/Icons";
import { useCountriesList } from "../../../util/Locations";
import { getCountryOption } from "../../../util/location";
import { ActionMeta } from "react-select";

const SectionTitle = styled.div`
  box-sizing: border-box;
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_2xl")};
  align-items: center;
  padding: ${({ theme }) => theme.resolve("spacing_xs")} 0;
  position: relative;
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  width: 100%;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
`;

const SectionTitleText = styled.h1`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_2xl")};
  line-height: 100%;
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  white-space: nowrap;
  letter-spacing: -0.5px;
  margin: 0;
  padding: 0;
`;

const ActionCellWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

type Props = {
  url: string;
  initialSubscribers: LeadAssignee[];
  onSuccess: () => void;
  onError: (error: unknown) => void;
};

const MultiValue = () => null;

export function AssignLeadSubscribers({
  url,
  initialSubscribers,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tableData, setTableData] = useState(() =>
    initialSubscribers.map((user) => ({
      ...user,
      label: convertUserToOption(user).label,
    }))
  );
  const [selectedUsers, setSelectedUsers] = useState(() =>
    initialSubscribers.map((user) => ({
      label: convertUserToOption(user).label,
      value: user,
    }))
  );
  const theme = useTheme();
  const countries = useCountriesList();

  const { handleSubmit } = useForm();

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      const subscriber_ids = selectedUsers.map((user) => user.value.id);
      await axios.patch(url, { subscriber_ids });
      onSuccess();
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserSelect = (
    options: OptionType<LeadAssignee>[] | null,
    actionMeta: ActionMeta<OptionType<LeadAssignee>>
  ) => {
    const selected = options || [];
    if (selected.length <= 5 && actionMeta.action !== "pop-value") {
      setSelectedUsers(selected);
    }
  };

  const handleRemoveUser = (userId: UUID) => {
    setSelectedUsers((currentUsers) =>
      currentUsers.filter((user) => user.value.id !== userId)
    );
  };

  const columns = useMemo<ColumnDef<LeadAssignee & { label: string }>[]>(
    () => [
      {
        id: "userName",
        header: t("Username"),
        accessorFn: (row) => row.label.split(" - ")[0],
      },
      {
        id: "emailId",
        header: t("Email ID"),
        accessorFn: (row) => row?.email_address || row.label.split(" - ")[1],
      },
      {
        id: "actions",
        header: t("Actions"),
        cell: ({ row }) => {
          const user = row.original;
          const tooltipId = `user-info-${user.id}`;

          const businessUnit = user?.business_unit || "N/A";
          const country = user?.country
            ? getCountryOption(countries, user.country)?.label
            : "N/A";

          return (
            <ActionCellWrapper>
              <IconButton type="button" data-tip data-for={tooltipId}>
                <InfoIcon fill={theme.resolve("color_icon_primary")} />
              </IconButton>
              <ReactTooltip id={tooltipId} effect="solid" multiline={true}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    alignItems: "flex-start",
                  }}
                >
                  <span>Business Unit: {businessUnit}</span>
                  <span>Country: {country}</span>
                </div>
              </ReactTooltip>
              <IconButton
                type="button"
                onClick={() => handleRemoveUser(user.id)}
              >
                <DeleteIcon fill={theme.errorColor} />
              </IconButton>
            </ActionCellWrapper>
          );
        },
      },
    ],
    [countries, t, theme]
  );

  useEffect(() => {
    setTableData(
      selectedUsers.map((user) => ({ ...user.value, label: user.label }))
    );
  }, [selectedUsers]);

  return (
    <>
      <SectionTitle>
        <SectionTitleText>{t("Assign Lead Subscriber(s)")}</SectionTitleText>
      </SectionTitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <SearchSelectInfiniteScroll
          label={t("Select Users (max 5)")}
          placeholder={t("Select Users (max 5)")}
          isMulti
          value={selectedUsers}
          onChange={handleUserSelect}
          baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
            storefront_id,
            tenant_id
          )}
          getOptions={(response: User[]) =>
            response
              .filter(
                (user) => !selectedUsers.some((u) => u.value.id === user.id)
              )
              .map((user) => ({
                label: convertUserToOption(user).label,
                value: user,
              }))
          }
          params={new URLSearchParams({ is_active: "true" })}
          filterOption={() => true}
          formState={{ submitCount: 0 }}
          errors={{}}
          components={{ MultiValue }}
        />

        {tableData.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <Table
              columns={columns}
              data={tableData}
              isLoading={false}
              error={undefined}
              rowHover={rowHover}
            />
          </div>
        )}

        <PrimaryButtonFitContainer
          type="submit"
          loading={isSubmitting}
          style={{ marginTop: "20px" }}
        >
          {t("Save")}
        </PrimaryButtonFitContainer>
      </Form>
    </>
  );
}
